<template>
  <div class="error-page relative flex min-h-screen flex-col overflow-hidden">
    <Head :title="documentTitle" />

    <div class="error-page__bg" aria-hidden="true" />
    <div class="error-page__glow error-page__glow--one" aria-hidden="true" />
    <div class="error-page__glow error-page__glow--two" aria-hidden="true" />

    <div class="relative z-10 flex flex-1 flex-col items-center justify-center px-5 py-12 sm:px-8">
      <Link href="/" class="mb-8 inline-flex sm:mb-10">
        <img
          src="@/images/components/cmslogo-new.png"
          alt="CODE Centrum Medyczne"
          class="h-12 w-auto sm:h-14"
        >
      </Link>

      <div class="flex w-full max-w-lg flex-col items-center text-center">
        <p
          class="select-none text-[7.5rem] font-bold leading-none tracking-[-0.06em] text-[#2ea8cf]/20 sm:text-[10rem]"
          aria-hidden="true"
        >
          {{ status }}
        </p>

        <div class="-mt-8 flex flex-col items-center gap-3 sm:-mt-10 sm:gap-4">
          <p class="text-sm font-semibold uppercase tracking-[1px] text-[#0c4a6e]/80">
            {{ status }}
          </p>
          <h1 class="text-[28px] font-medium leading-9 tracking-[-0.02em] text-[#262626] sm:text-[36px] sm:leading-[44px]">
            {{ title }}
          </h1>
          <p class="max-w-md text-base leading-6 text-neutral-600 sm:text-lg sm:leading-7">
            {{ description }}
          </p>
        </div>

        <div class="mt-8 flex w-full flex-col items-stretch gap-3 sm:mt-10 sm:w-auto sm:flex-row sm:items-center">
          <button
            v-if="canGoBack"
            type="button"
            class="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-[#b3e0ee] bg-white px-5 text-base font-semibold leading-[18px] text-[#0c4a6e] shadow-sm transition-colors hover:bg-[#d5eef6] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2ea8cf] focus-visible:ring-offset-2"
            @click="goBack"
          >
            <Icon name="cheveron-left" class="size-4 fill-current" />
            {{ goBackLabel }}
          </button>

          <Link
            href="/"
            class="inline-flex h-12 items-center justify-center gap-2 rounded-lg px-5 text-base font-semibold leading-[18px] text-white no-underline shadow-sm transition-opacity hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2ea8cf] focus-visible:ring-offset-2"
            :style="{ backgroundImage: brandGradient }"
          >
            {{ backHomeLabel }}
          </Link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3';
import Icon from '@/Shared/Icon.vue';
import { BRAND_GRADIENT } from '@/utils/siteThemeColors';

const FALLBACKS = {
  back_home: 'Wróć do strony głównej',
  go_back: 'Wróć',
  403: {
    title: 'Brak dostępu',
    description: 'Nie masz uprawnień, aby wyświetlić tę stronę.',
  },
  404: {
    title: 'Nie znaleziono strony',
    description: 'Przepraszamy, strona której szukasz nie istnieje lub została przeniesiona.',
  },
  500: {
    title: 'Błąd serwera',
    description: 'Ups, coś poszło nie tak po naszej stronie. Spróbuj ponownie za chwilę.',
  },
  503: {
    title: 'Trwają prace serwisowe',
    description: 'Przepraszamy, prowadzimy właśnie konserwację. Wróć do nas wkrótce.',
  },
};

export default {
  components: {
    Head,
    Link,
    Icon,
  },
  props: {
    status: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      brandGradient: BRAND_GRADIENT,
      canGoBack: false,
    };
  },
  computed: {
    copy() {
      return FALLBACKS[this.status] || FALLBACKS[404];
    },
    title() {
      return this.tOrFallback(`errors.${this.status}.title`, this.copy.title);
    },
    description() {
      return this.tOrFallback(`errors.${this.status}.description`, this.copy.description);
    },
    backHomeLabel() {
      return this.tOrFallback('errors.back_home', FALLBACKS.back_home);
    },
    goBackLabel() {
      return this.tOrFallback('errors.go_back', FALLBACKS.go_back);
    },
    documentTitle() {
      return `${this.status}: ${this.title}`;
    },
  },
  mounted() {
    this.canGoBack = window.history.length > 1;
  },
  methods: {
    tOrFallback(key, fallback) {
      const value = this.$t(key);
      return !value || value === key ? fallback : value;
    },
    goBack() {
      if (window.history.length > 1) {
        window.history.back();
        return;
      }
      this.$inertia.visit('/');
    },
  },
};
</script>

<style scoped>
.error-page {
  background: #f7fbfd;
}

.error-page__bg {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(120% 80% at 50% -10%, rgba(46, 168, 207, 0.18), transparent 55%),
    linear-gradient(180deg, #e8f6fb 0%, #f7fbfd 45%, #ffffff 100%);
}

.error-page__glow {
  position: absolute;
  border-radius: 9999px;
  filter: blur(64px);
  pointer-events: none;
}

.error-page__glow--one {
  top: 12%;
  left: -8%;
  width: min(420px, 70vw);
  height: min(420px, 70vw);
  background: rgba(46, 168, 207, 0.22);
}

.error-page__glow--two {
  right: -10%;
  bottom: 8%;
  width: min(380px, 65vw);
  height: min(380px, 65vw);
  background: rgba(40, 182, 155, 0.18);
}
</style>
